import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { startOfDay, startOfMonth, startOfWeek, startOfYear } from "date-fns";
import { db } from "../config/firebase";

const DEFAULT_TARGET = {
  annualTarget: 0,
  monthlyTarget: 0,
  weeklyTarget: 0,
  dailyTarget: 0,
  currency: "XOF",
};

const DEFAULT_OBJECTIVE = {
  annualTarget: 0,
  monthlyTarget: 0,
  weeklyTarget: 0,
  dailyTarget: 0,
  currency: "XOF",
  latestObservation: null,
  observationCount: 0,
};

const sanitizeNumber = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value.replace(/\s/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const toDate = (input) => {
  if (!input) return null;
  if (typeof input.toDate === "function") {
    try {
      return input.toDate();
    } catch (err) {
      return null;
    }
  }
  const date = new Date(input);
  return Number.isNaN(date.getTime()) ? null : date;
};

const buildTargetPayload = (data, adminId) => ({
  annualTarget: sanitizeNumber(data.annualTarget),
  monthlyTarget: sanitizeNumber(data.monthlyTarget),
  weeklyTarget: sanitizeNumber(data.weeklyTarget),
  dailyTarget: sanitizeNumber(data.dailyTarget),
  currency: data.currency || DEFAULT_TARGET.currency,
  updatedAt: serverTimestamp(),
  updatedBy: adminId,
});

const buildObjectivePayload = (data, adminId) => ({
  annualTarget: sanitizeNumber(data.annualTarget),
  monthlyTarget: sanitizeNumber(data.monthlyTarget),
  weeklyTarget: sanitizeNumber(data.weeklyTarget),
  dailyTarget: sanitizeNumber(data.dailyTarget),
  currency: data.currency || DEFAULT_OBJECTIVE.currency,
  assignedAt: serverTimestamp(),
  assignedBy: adminId,
});

const normalizeDoc = (snapshot, fallback) => {
  if (snapshot.exists()) {
    return { id: snapshot.id, ...fallback, ...snapshot.data() };
  }
  return { id: snapshot.id, ...fallback };
};

async function fetchContractsForUser(userId) {
  const contractsRef = collection(db, "contracts");
  const q = query(contractsRef, where("assignedToUserId", "==", userId));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  }));
}

function computeRevenueProgress(contracts) {
  const now = new Date();
  const dayStart = startOfDay(now);
  const weekStart = startOfWeek(now, { weekStartsOn: 1 });
  const monthStart = startOfMonth(now);
  const yearStart = startOfYear(now);

  return contracts.reduce(
    (acc, contract) => {
      const signedAt = toDate(contract.signedAt) || toDate(contract.createdAt);
      if (!signedAt) {
        return acc;
      }

      const amount = sanitizeNumber(contract.amount);

      if (amount <= 0) {
        return acc;
      }

      acc.total += amount;

      if (signedAt >= yearStart) {
        acc.annual += amount;
      }

      if (signedAt >= monthStart) {
        acc.monthly += amount;
      }

      if (signedAt >= weekStart) {
        acc.weekly += amount;
      }

      if (signedAt >= dayStart) {
        acc.daily += amount;
      }

      return acc;
    },
    { daily: 0, weekly: 0, monthly: 0, annual: 0, total: 0 }
  );
}

export const revenueService = {
  async getGlobalTarget() {
    try {
      const ref = doc(db, "revenueTargets", "global");
      const snapshot = await getDoc(ref);
      return normalizeDoc(snapshot, DEFAULT_TARGET);
    } catch (error) {
      console.error("Erreur récupération du target global:", error);
      return { id: "global", ...DEFAULT_TARGET };
    }
  },

  async upsertGlobalTarget(targetData, adminId) {
    const ref = doc(db, "revenueTargets", "global");
    const payload = buildTargetPayload(targetData, adminId);
    await setDoc(ref, payload, { merge: true });
    return payload;
  },

  async getObjectiveForUser(userId) {
    try {
      const ref = doc(db, "revenueObjectives", userId);
      const snapshot = await getDoc(ref);
      return normalizeDoc(snapshot, { ...DEFAULT_OBJECTIVE, userId });
    } catch (error) {
      console.error("Erreur récupération objectif utilisateur:", error);
      return { id: userId, userId, ...DEFAULT_OBJECTIVE };
    }
  },

  async setObjectiveForUser(userId, objectiveData, adminId) {
    const ref = doc(db, "revenueObjectives", userId);
    const payload = {
      userId,
      ...buildObjectivePayload(objectiveData, adminId),
    };
    await setDoc(ref, payload, { merge: true });
    return payload;
  },

  async getAllObjectives() {
    try {
      const snapshot = await getDocs(collection(db, "revenueObjectives"));
      return snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        userId: docSnap.id,
        ...DEFAULT_OBJECTIVE,
        ...docSnap.data(),
      }));
    } catch (error) {
      console.error("Erreur récupération objectifs:", error);
      return [];
    }
  },

  async addObservation(userId, { message, level = "info" }, adminId) {
    const cleanMessage = message?.trim();
    if (!cleanMessage) {
      throw new Error("Le message d'observation est requis");
    }

    const payload = {
      userId,
      message: cleanMessage,
      level,
      createdAt: serverTimestamp(),
      createdBy: adminId,
    };

    const observationsRef = collection(db, "revenueObservations");
    const docRef = await addDoc(observationsRef, payload);

    await setDoc(
      doc(db, "revenueObjectives", userId),
      {
        userId,
        latestObservation: { ...payload, id: docRef.id },
        lastObservationLevel: level,
        lastObservationAt: serverTimestamp(),
        observationCount: increment(1),
      },
      { merge: true }
    );

    return { id: docRef.id, ...payload };
  },

  async getObservations(userId, maxItems = 20) {
    try {
      const observationsRef = collection(db, "revenueObservations");
      const q = query(
        observationsRef,
        where("userId", "==", userId),
        orderBy("createdAt", "desc"),
        limit(maxItems)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
    } catch (error) {
      console.error("Erreur récupération observations:", error);
      return [];
    }
  },

  async getUserProgress(userId) {
    try {
      const contracts = await fetchContractsForUser(userId);
      return computeRevenueProgress(contracts);
    } catch (error) {
      console.error("Erreur calcul progression utilisateur:", error);
      return { daily: 0, weekly: 0, monthly: 0, annual: 0, total: 0 };
    }
  },

  async getTeamProgress({ includeUsers = true } = {}) {
    try {
      const [objectives, usersSnapshot] = await Promise.all([
        this.getAllObjectives(),
        includeUsers ? getDocs(collection(db, "USERS")) : Promise.resolve(null),
      ]);

      const users = new Map();
      if (usersSnapshot) {
        usersSnapshot.forEach((docSnap) => {
          users.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
        });
      }

      const targetUsers = includeUsers
        ? Array.from(users.values()).filter(
            (user) => user.role === "commercial"
          )
        : objectives.map((objective) => ({ id: objective.userId }));

      const progressEntries = await Promise.all(
        targetUsers.map(async (user) => {
          const objective = objectives.find(
            (item) => item.userId === user.id
          ) || { userId: user.id, ...DEFAULT_OBJECTIVE };
          const progress = await this.getUserProgress(user.id);
          return {
            userId: user.id,
            user,
            objective,
            progress,
          };
        })
      );

      return progressEntries;
    } catch (error) {
      console.error("Erreur récupération progression équipe:", error);
      return [];
    }
  },
};
